<template>
  <score-card
    :title="'Table ' + match.tableNumber"
    :teams="teamMembers"
    :goals="teamGoals"
    :max-score-button="maxVals"
    :has-scores-been-submitted="hasGoals"
    :active="!!match.active"
    :clickable="isClickable"
    :is-creator="isCreator"
    :match-age="matchAge"
    :submitting="submitting"
    @destroy="$emit('cancel')"
    @score="(ti, v) => submitScore(ti, v)"
    @ironman-score="(ti) => submitScore(ti)"
  />
</template>

<script>
import ScoreCard from "@/components/score-card.vue";
import { acquireScoringLock } from "@/utils/scoring-lock";
import { isScorecardStale } from "@/utils/scorecard-guard";
import { MATCH_WINNING_SCORE } from "@/utils/league-constants";

export default {
  name: "MatchCard",
  components: {
    ScoreCard
  },
  props: {
    match: {
      type: Object,
      required: true
    },
    isCreator: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel"],
  data () {
    return {
      now: Date.now(),
      timerHandle: null,
      submitting: false
    };
  },
  mounted () {
    this.timerHandle = setInterval(() => { this.now = Date.now(); }, 1000);
  },
  beforeUnmount () {
    clearInterval(this.timerHandle);
  },
  computed: {
    matchAge () {
      if (!this.match.active || !this.match.createdAt) return "";
      const elapsed = Math.floor((this.now - this.match.createdAt.toDate()) / 1000);
      if (elapsed < 0) return "0:00";
      const hours = Math.floor(elapsed / 3600);
      const minutes = Math.floor((elapsed % 3600) / 60);
      const seconds = elapsed % 60;
      const pad = (n) => n.toString().padStart(2, "0");
      if (hours > 0) {
        return `${hours}:${pad(minutes)}:${pad(seconds)}`;
      }
      return `${minutes}:${pad(seconds)}`;
    },
    isClickable () {
      return this.match.active || this.isCreator;
    },
    hasGoals () {
      if (!this.match.teams) return false;
      return this.match.teams.some(t => t.goals > 0) || !this.match.active;
    },
    teamMembers () {
      if (!this.match.teams) return [];
      return this.match.teams.map(t => t.players);
    },
    teamGoals () {
      if (!this.match.teams) return [];
      return this.match.teams.map(t =>
        t.goals > 0 || !this.match.active ? t.goals : undefined
      );
    },
    maxVals () {
      if (!this.match.teams) return [];
      return this.match.teams.map(() => MATCH_WINNING_SCORE);
    }
  },
  methods: {
    async shouldAutoGenerateNextRound (tournament) {
      if (!tournament.callMatchesAutomatically) return false;
      const { collection, getDocs, query, where } = tournament.dbFns;
      const q = query(
        collection(tournament.db, "matches"),
        where("tournamentId", "==", tournament.id)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.filter(d => d.data().active).length === 0;
    },
    async submitScore (teamIndex, losingScore) {
      if (this.submitting) return;
      this.submitting = true;

      try {
        await acquireScoringLock(this.match.tournament.docRef, this.match.tournament.db, this.match.tournament.dbFns);
      } catch (lockError) {
        this.submitting = false;
        throw lockError;
      }

      try {
        const blocked = await this.blockedPlayers();
        if (blocked.length > 0) {
          const proceed = window.confirm(
            `These players have games in another competition that will no longer count toward their rating:\n${blocked.map(player => player.name).join(", ")}\n\n` +
            "To avoid this, don't score multiple tournaments or league races at the same time.\n\n" +
            "Continue submitting?"
          );
          if (!proceed) return;
        }

        if (this.match.teams.length === 2) {
          await this.submitTwoTeamScore(this.match, teamIndex, losingScore);
        } else if (this.match.teams.length === 3) {
          await this.submitThreeTeamScore(this.match, teamIndex);
        }

        this.expanded = false;
        this.showScore = true;

        await Promise.all([
          this.recalcAllScorecards(),
          this.match.tournament.callMatchesAutomatically
            ? this.shouldAutoGenerateNextRound(this.match.tournament).then(shouldGenerate => {
              if (shouldGenerate) return this.match.tournament.generateNextRound();
            })
            : false
        ]);
      } finally {
        await this.match.tournament.dbFns.updateDoc(this.match.tournament.docRef, { scoringLock: false });
        this.submitting = false;
      }
    },
    async submitTwoTeamScore (match, losingTeamIndex, losingScore) {
      const winningTeamIndex = losingTeamIndex === 0 ? 1 : 0;
      const winningScore = MATCH_WINNING_SCORE;

      const losingTeam = match.teams[losingTeamIndex];
      const winningTeam = match.teams[winningTeamIndex];

      await Promise.all([
        losingTeam.immediateUpdate({ goals: losingScore }),
        winningTeam.immediateUpdate({ goals: winningScore }),
        match.immediateUpdate({ active: false })
      ]);
    },
    async submitThreeTeamScore (match, winningTeamIndex) {
      const teams = match.teams;

      const teamPromises = teams.map((team, ti) => {
        if (ti === winningTeamIndex) {
          return team.immediateUpdate({ goals: 1 });
        }
        return team.immediateUpdate({ goals: 0 });
      });

      await Promise.all([
        ...teamPromises,
        match.immediateUpdate({ active: false })
      ]);
    },
    async blockedPlayers () {
      const tournament = this.match.tournament;
      await Promise.all(
        tournament.players.map(async player => {
          await player.promiseUserModel;
          player.userModel.scorecard;
          await player.userModel.promiseScorecard;
        })
      );
      return tournament.players.filter(player =>
        isScorecardStale(player.userModel.scorecard.updatedAt, tournament.ratingsSyncedAt)
      );
    },
    async recalcAllScorecards () {
      const tournament = this.match.tournament;
      await Promise.all(
        tournament.players.map(async p => {
          await p.promiseUserModel;
          p.userModel.scorecard;
          await p.userModel.promiseScorecard;
        })
      );
      const playerRatings = tournament.playerRatings;
      const updatedAt = new Date();
      await Promise.all(
        tournament.players.map(p =>
          p.userModel.scorecard.immediateUpdate({
            mu: playerRatings[p.id].mu,
            sigma: playerRatings[p.id].sigma,
            updatedAt
          })
        )
      );
      await tournament.immediateUpdate({ ratingsSyncedAt: new Date() });
    }
  }
};
</script>
